// ══════════════════════════════════════════════════════════════════
// SecStatut.jsx — Statut des appartements (étapes 1, 2, 3 + correctifs)
// Étape 3+ : fils 📢 Partagé, 💬 Proprio, 🔒 Interne (lecture)
//            + pastilles filtres cliquables + pastille En cours
//            + correction affichage rapport sur "À nettoyer"
//            + bouton → Voir le rapport (navigation vers ctrl)
// ══════════════════════════════════════════════════════════════════

import React from 'react';
import { useState, useMemo } from 'react';
import { today, fd } from '../utils.js';
import { SUPA_URL, SUPA_KEY } from '../constants.js';
import { supaAuthHeader } from '../supabase.js';

// ─── Helpers heure ────────────────────────────────────────────────
function heureMaintenantMin() {
  const n = new Date();
  return n.getHours() * 60 + n.getMinutes();
}
function heureStrToMin(h) {
  if (!h) return null;
  const [hh, mm] = h.split(':').map(Number);
  return hh * 60 + (mm || 0);
}
function nowIso() { return new Date().toISOString(); }

// ─── Helpers : arrivées/départs d'un bien pour une date ──────────
function getArriveesBien(bid, dateStr, sejours, statutAppts) {
  const result = [];
  if ((sejours||[]).some(s=>s.bid===bid&&s.statut!=='ignore'&&s.dateArrivee===dateStr))
    result.push({src:'ical',heure:'16:00'});
  const key=`ad_arr_${dateStr}`;
  ((statutAppts||{})[key]||[]).filter(e=>e.bid===bid).forEach(e=>result.push({src:'manual',heure:e.heure||'16:00'}));
  return result;
}

function getDepartsBien(bid, dateStr, sejours, statutAppts) {
  const result = [];
  if ((sejours||[]).some(s=>s.bid===bid&&s.statut!=='ignore'&&s.dateDepart===dateStr))
    result.push({src:'ical',heure:'10:00'});
  const key=`ad_dep_${dateStr}`;
  ((statutAppts||{})[key]||[]).filter(e=>e.bid===bid).forEach(e=>result.push({src:'manual',heure:e.heure||'10:00'}));
  return result;
}

function toutesArriveesBien(bid, sejours, st, statutAppts, als) {
  const dates = new Set();
  (sejours||[]).filter(s=>s.bid===bid&&s.statut!=='ignore'&&s.dateArrivee).forEach(s=>dates.add(s.dateArrivee));
  const todayStr = today();
  const nowMin = heureMaintenantMin();
  (st||[]).filter(t=>t.bid===bid&&t.type==='checkin'&&t.date).forEach(t=>{
    if (t.date < todayStr) {
      dates.add(t.date);
    } else if (t.date === todayStr) {
      // Arrivée du jour : on l'expose si l'heure prévue est atteinte OU si le check-in est déjà pointé.
      const tacheMin = heureStrToMin(`${String(t.sh).padStart(2,'0')}:${String(t.sm||0).padStart(2,'0')}`);
      const pointe = (als||[]).some(a=>a.tid===t.id);
      if (pointe || (tacheMin !== null && nowMin >= tacheMin)) dates.add(t.date);
    }
  });
  Object.entries(statutAppts||{}).filter(([k])=>k.startsWith('ad_arr_')).forEach(([k,entries])=>{
    const d=k.replace('ad_arr_','');
    if((entries||[]).some(e=>e.bid===bid)) dates.add(d);
  });
  return [...dates].sort((a,b)=>b.localeCompare(a));
}

function tousDeparts(bid, sejours, statutAppts) {
  const dates = new Set();
  (sejours||[]).filter(s=>s.bid===bid&&s.statut!=='ignore'&&s.dateDepart).forEach(s=>dates.add(s.dateDepart));
  Object.entries(statutAppts||{}).filter(([k])=>k.startsWith('ad_dep_')).forEach(([k,entries])=>{
    const d=k.replace('ad_dep_','');
    if((entries||[]).some(e=>e.bid===bid)) dates.add(d);
  });
  return [...dates].sort((a,b)=>a.localeCompare(b));
}

function departDuJour(bid, dateStr, sejours, statutAppts) {
  const deps = getDepartsBien(bid, dateStr, sejours, statutAppts);
  return deps.length > 0 ? deps[0] : null;
}

function arriveeAujourdhui(bid, dateStr, sejours, st, statutAppts) {
  if ((sejours||[]).some(s=>s.bid===bid&&s.statut!=='ignore'&&s.dateArrivee===dateStr)) return true;
  const key=`ad_arr_${dateStr}`;
  if (((statutAppts||{})[key]||[]).some(e=>e.bid===bid)) return true;
  return (st||[]).some(t=>t.bid===bid&&t.type==='checkin'&&t.date===dateStr);
}

function prochainSejour(bid, sejours, st, statutAppts, als) {
  const todayStr = today();
  const futures = toutesArriveesBien(bid, sejours, st, statutAppts, als).filter(d=>d>todayStr);
  if (futures.length === 0) return null;
  const date = futures[futures.length - 1];
  const fromIcal = (sejours||[]).some(s=>s.bid===bid&&s.statut!=='ignore'&&s.dateArrivee===date);
  let heure = null;
  if (fromIcal) {
    heure = '16:00';
  } else {
    const key=`ad_arr_${date}`;
    const entries=((statutAppts||{})[key]||[]).filter(e=>e.bid===bid);
    if (entries.length>0) heure=entries[0].heure||'16:00';
    const tache=(st||[]).find(t=>t.bid===bid&&t.type==='checkin'&&t.date===date);
    if (tache) heure=`${String(tache.sh).padStart(2,'0')}:${String(tache.sm||0).padStart(2,'0')}`;
  }
  return { date, heure };
}

// ─── Détection ménage en cours / terminé via pointages als ──────
// Types ménage (cohérent avec isMenage dans GerantClientUI)
const TYPES_MENAGE = ['full','quick','linge','correction'];

// En cours : pointage start posé sans end correspondant
function menageEnCours(bid, als, st) {
  const todayStr = today();
  const tachesMenage = (st||[]).filter(t=>t.bid===bid&&t.date===todayStr&&TYPES_MENAGE.includes(t.type));
  if (tachesMenage.length===0) return false;
  const tids = new Set(tachesMenage.map(t=>t.id));
  return (als||[]).some(a=>{
    if (!tids.has(a.tid)||a.type!=='start') return false;
    return !(als||[]).some(b=>b.tid===a.tid&&b.type==='end');
  });
}

// Terminé : pointage end sur une tâche ménage, OU tâche ménage cochée « Fait »
// (st==='done') dans le planning — cas panne de batterie : ni pointage ni rapport,
// l'admin coche le ménage terminé à la main. Fenêtre = 7 derniers jours.
// Retourne le marqueur de fin le plus récent (timestamp ISO du pointage ou date de
// la tâche cochée), ou false. La validité vs dernier départ est vérifiée en aval.
function menageTermine(bid, als, st) {
  const cutoff = new Date(Date.now()-7*24*3600*1000).toISOString().slice(0,10);
  const tachesMenage = (st||[]).filter(t=>t.bid===bid&&t.date>=cutoff&&TYPES_MENAGE.includes(t.type));
  if (tachesMenage.length===0) return false;
  const tids = new Set(tachesMenage.map(t=>t.id));
  // 1) pointage « end » le plus récent sur une de ces tâches
  const ends = (als||[]).filter(a=>tids.has(a.tid)&&a.type==='end').sort((a,b)=>b.ts.localeCompare(a.ts));
  const dernierEnd = ends.length ? ends[0].ts : null;
  // 2) tâche ménage cochée « Fait » la plus récente (par date)
  const faits = tachesMenage.filter(t=>t.st==='done').map(t=>t.date).sort((a,b)=>b.localeCompare(a));
  const dernierFait = faits.length ? faits[0] : null;
  // Marqueur = le plus récent des deux. Comparaison lexicographique OK : les deux
  // commencent par YYYY-MM-DD et dernierDepart (comparé en aval) est une date pure.
  const marqueurs = [dernierEnd, dernierFait].filter(Boolean);
  if (marqueurs.length===0) return false;
  return marqueurs.sort((a,b)=>b.localeCompare(a))[0];
}

// Check-in pointé : un pointage als (start OU end) existe sur une tâche check-in du bien
// à la date donnée. Sert à décider si une arrivée prévue est devenue une occupation réelle.
function checkinPointe(bid, dateStr, als, st) {
  const tachesCheckin = (st||[]).filter(t=>t.bid===bid&&t.date===dateStr&&t.type==='checkin');
  if (tachesCheckin.length===0) return false;
  const tids = new Set(tachesCheckin.map(t=>t.id));
  return (als||[]).some(a=>tids.has(a.tid));
}

// Une arrivée prévue le jour même est-elle "effective" ?
//  - S'il existe une tâche check-in ce jour : effective seulement si le check-in est pointé.
//  - Sinon (arrivée iCal ou manuelle, sans service check-in, ex. Delphine) : effective à partir de 16h.
function arriveeEffectiveAujourdhui(bid, dateStr, als, st) {
  const aTacheCheckin = (st||[]).some(t=>t.bid===bid&&t.date===dateStr&&t.type==='checkin');
  if (aTacheCheckin) {
    return checkinPointe(bid, dateStr, als, st);
  }
  // Pas de tâche check-in : bascule en occupé à 16h00
  return heureMaintenantMin() >= 16*60;
}

// ─── Calcul statut d'un bien ──────────────────────────────────────
function calcStatutBien(bid, sejours, rep, controles, statutAppts, st, als) {
  const todayStr = today();
  const overrides = {};
  Object.entries(statutAppts||{}).forEach(([k,v])=>{ if(!k.startsWith('ad_'))overrides[k]=v; });
  const override = overrides?.[bid];
  // Horodatage de pose de l'override manuel (force_pret/force_ctrl/...), écrit par
  // handleSetStatutManuel sous la clé `force_ts_{bid}`. Sert à le faire expirer
  // automatiquement au prochain départ détecté (un override = un cycle, jamais à
  // réinitialiser manuellement).
  const overrideTs = overrides?.[`force_ts_${bid}`] || null;

  const dep = departDuJour(bid, todayStr, sejours, statutAppts);
  if (dep) {
    const depMin = heureStrToMin(dep.heure||'10:00');
    if (depMin !== null && heureMaintenantMin() < depMin) {
      return { code:'sortie', emoji:'🔴', label:'Sortie en cours', color:'#dc2626', bg:'#fde0e0', heureDepart:dep.heure||'10:00' };
    }
  }

  const toutes = toutesArriveesBien(bid, sejours, st, statutAppts, als);
  const departsSet = new Set(tousDeparts(bid, sejours, statutAppts));
  let occupeArr = null;
  let prochainDep = null;
  for (const d of toutes) {
    if (d > todayStr) continue;
    let found = false;
    for (const dd of departsSet) {
      if (dd > d) { found = true; prochainDep = dd; break; }
    }
    if (found) { occupeArr = d; break; }
    if (!found) {
      const hasDepart = [...departsSet].some(dd=>dd>d);
      if (!hasDepart) { occupeArr = d; break; }
    }
  }

  const sejourCourant = (sejours||[]).find(s=>
    s.bid===bid && s.statut!=='ignore' &&
    s.dateArrivee <= todayStr && s.dateDepart > todayStr
  );

  // Occupation candidate selon les arrivées/départs détectés
  const occCandidate = sejourCourant || (occupeArr && (!prochainDep || prochainDep > todayStr) && occupeArr <= todayStr);

  // Si l'occupation repose sur une arrivée datée d'AUJOURD'HUI, elle ne devient effective que :
  //   - quand le check-in est pointé (s'il existe une tâche check-in), OU
  //   - à partir de 16h (arrivée sans service check-in, ex. Delphine).
  // Une arrivée antérieure (séjour déjà en cours) reste occupée sans condition.
  const arriveeRef = sejourCourant ? sejourCourant.dateArrivee : occupeArr;
  let isOcc = occCandidate;
  if (occCandidate && arriveeRef === todayStr) {
    isOcc = arriveeEffectiveAujourdhui(bid, todayStr, als, st);
  }

  const rapportSoumis = (rep||[]).filter(r=>r.bid===bid&&r.sub&&!r.hidAdmin)
    .sort((a,b)=>b.sub.localeCompare(a.sub))[0]||null;

  // Contrôle valide = pret, nonpret, ou abandonne (tous clôturent le cycle)
  const controle = (controles||[]).filter(c=>c.bid===bid&&(c.verdict==='pret'||c.verdict==='nonpret'||c.verdict==='abandonne'))
    .sort((a,b)=>(b.closedAt||'').localeCompare(a.closedAt||''))[0]||null;

  // Contrôle en attente = créé pour ce bien mais sans verdict encore posé (ni clos).
  // Sert de garde-fou : ne jamais retomber sur "Prêt" par défaut si un contrôle est en cours.
  const controleEnAttente = (controles||[]).some(c=>c.bid===bid && !c.verdict && !c.closedAt);

  const dernierDepart = tousDeparts(bid,sejours,statutAppts).filter(d=>d<=todayStr).sort((a,b)=>b.localeCompare(a))[0]||null;
  const dernierSejour = dernierDepart
    ? (sejours||[]).find(s=>s.bid===bid&&s.dateDepart===dernierDepart) || null
    : null;

  // Rapport valide = postérieur au dernier départ connu
  const rapportValide = rapportSoumis && dernierDepart
    ? rapportSoumis.sub >= dernierDepart
    : rapportSoumis;
  // Contrôle valide = postérieur au dernier départ connu. On conserve l'OBJET contrôle
  // (pas un booléen) pour pouvoir lire son verdict ensuite.
  // Exception : un contrôle abandonné sans closedAt est valide si le rapport est valide.
  const controleValide = controle
    ? (dernierDepart
        ? ((controle.closedAt||'') >= dernierDepart || (controle.verdict==='abandonne' && rapportValide) ? controle : null)
        : controle)
    : null;

  let derniereArrivee = null;
  if (isOcc && !sejourCourant) {
    derniereArrivee = toutes.filter(d=>d<=todayStr)[0]||null;
  }

  // Ménage en cours : start sans end, ou forcé manuellement par l'admin
  // Le force_encours est ignoré si un end de ménage a déjà été posé (ménage terminé)
  const finMenage = !isOcc && menageTermine(bid, als, st);
  const enCoursForce = !isOcc && !finMenage && !!(statutAppts?.[`force_encours_${bid}`]);
  // 🔵 En cours étouffé si le ménage est déjà terminé (pointage fin OU coché « Fait ») :
  // même garde que enCoursForce, étendue à la détection automatique menageEnCours.
  const enCours = !isOcc && !finMenage && (menageEnCours(bid, als, st) || enCoursForce);
  // finMenage est valide uniquement si postérieur au dernier départ
  const finMenageValide = finMenage && dernierDepart
    ? finMenage >= dernierDepart
    : finMenage;

  // Override manuel valide = pas occupé (déjà filtré par isOcc plus bas) ET pas expiré.
  // Expire dès qu'un départ postérieur à sa pose est détecté pour ce bien (nouveau cycle).
  // Si aucun horodatage n'est trouvé (override posé avant l'ajout de ce mécanisme,
  // ex. l'ancienne valeur orpheline 'pret_force'), on le considère expiré par sécurité —
  // mieux vaut retomber sur le calcul automatique qu'un override sans date fiable.
  const overrideValide = override && overrideTs
    ? !(dernierDepart && dernierDepart > overrideTs.slice(0,10))
    : false;

  const base = { rapport:rapportSoumis, controle, controleValide, dernierDepart, dernierSejour,
    sejour:sejourCourant||null, derniereArrivee, prochainDepart:prochainDep, enCours };

  if (isOcc)
    return { code:'occ', emoji:'🔴', label:'Occupé', color:'#dc2626', bg:'#fde0e0', ...base };

  if (overrideValide && override==='force_pret')
    return { code:'pret', emoji:'🟢', label:'Prêt', color:'#16a34a', bg:'#e1f7ee', ...base };
  if (overrideValide && override==='force_ctrl')
    return { code:'ctrl', emoji:'🟡', label:'À contrôler', color:'#b45309', bg:'#fef3c7', ...base };
  if (overrideValide && override==='force_men')
    return { code:'men', emoji:'🟠', label:'À nettoyer', color:'#c2410c', bg:'#ffe9d6', ...base };
  if (overrideValide && override==='force_nonpret')
    return { code:'nonpret', emoji:'⚠️', label:'Problème signalé', color:'#b91c1c', bg:'#fde0e0', ...base };
  if (overrideValide && override==='force_sortie')
    return { code:'sortie', emoji:'🔴', label:'Sortie en cours', color:'#dc2626', bg:'#fde0e0', ...base };
  if (controleValide?.verdict==='pret')
    return { code:'pret', emoji:'🟢', label:'Prêt', color:'#16a34a', bg:'#e1f7ee', ...base };
  if (controleValide?.verdict==='nonpret')
    return { code:'nonpret', emoji:'⚠️', label:'Problème signalé', color:'#b91c1c', bg:'#fde0e0', ...base };
  // Contrôle abandonné = admin a décidé de ne pas contrôler → on sort du cycle, appartement prêt
  if (controleValide?.verdict==='abandonne')
    return { code:'pret', emoji:'🟢', label:'Prêt', color:'#16a34a', bg:'#e1f7ee', ...base };
  // Passage à 'ctrl' : pointage fin posé OU rapport soumis (le plus tôt des deux suffit)
  if (finMenageValide || rapportValide)
    return { code:'ctrl', emoji:'🟡', label:'À contrôler', color:'#b45309', bg:'#fef3c7', ...base };

  if (dernierDepart) {
    const jours = Math.floor((new Date(todayStr)-new Date(dernierDepart))/86400000);
    if (jours<=7) return { code:'men', emoji:'🟠', label:'À nettoyer', color:'#c2410c', bg:'#ffe9d6', dernierDepart, ...base };
  }

  // Garde-fou : un contrôle créé et non clos pour ce bien ne doit jamais retomber sur "Prêt"
  // par défaut, même si dernierDepart est absent/ancien ou si finMenageValide/rapportValide
  // sont faux pour une autre raison.
  if (controleEnAttente)
    return { code:'ctrl', emoji:'🟡', label:'À contrôler', color:'#b45309', bg:'#fef3c7', ...base };

  return { code:'pret', emoji:'🟢', label:'Prêt', color:'#16a34a', bg:'#e1f7ee', ...base };
}

// ─── Fil de commentaires (Partagé ou Proprio) ────────────────────
function FilCommentaires({ bid, fil, setFil, isAdmin, titre, icone, disclaimer, readOnly, clientNom, bienNom, hint }) {
  const [texte, setTexte] = useState('');
  const [saving, setSaving] = useState(false);

  const msgs = useMemo(()=>
    (fil||[]).filter(m=>m.bid===bid).sort((a,b)=>a.ts.localeCompare(b.ts)),
    [fil, bid]
  );

  const handleSend = () => {
    if (!texte.trim() || saving) return;
    setSaving(true);
    const msg = {
      id: 'm'+Date.now(),
      bid,
      auteur: isAdmin ? 'La Conciergerie' : 'Propriétaire',
      auteurType: isAdmin ? 'admin' : 'client',
      texte: texte.trim(),
      ts: nowIso(),
      lu_admin: isAdmin,
      lu_client: !isAdmin,
    };
    setFil(prev=>[...(prev||[]), msg]);
    setTexte('');
    setSaving(false);
    // Email de notification quand le propriétaire envoie un message
    if (!isAdmin) {
      const nomBien = bienNom || 'un logement';
      const nomClient = clientNom || 'Un propriétaire';
      const htmlContent =
        '<div style="font-family:Arial,Helvetica,sans-serif;font-size:14px;color:#2a2520;line-height:1.6;">'
        + `<p>Bonjour,</p>`
        + `<p><strong>${nomClient}</strong> a envoyé un message depuis son espace propriétaire concernant <strong>${nomBien}</strong> :</p>`
        + `<blockquote style="border-left:3px solid #FF8617;margin:12px 0;padding:8px 14px;background:#fff8f0;font-style:italic;border-radius:0 6px 6px 0;">${msg.texte.replace(/</g,'&lt;').replace(/>/g,'&gt;')}</blockquote>`
        + `<p style="margin-top:18px;"><a href="https://app.conciergerie-alsace.com" target="_blank" style="display:inline-block;background:#FF8617;color:#ffffff;text-decoration:none;font-weight:bold;padding:11px 22px;border-radius:10px;font-size:14px;">Voir dans l'app</a></p>`
        + '<p style="font-size:12px;color:#6b5d52;">La Conciergerie — Colmar, Alsace</p>'
        + '</div>';
      fetch(`${SUPA_URL}/functions/v1/send-invoice-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supaAuthHeader()}`,
          'apikey': SUPA_KEY,
        },
        body: JSON.stringify({
          toEmail: 'client@conciergerie-alsace.com',
          toName: 'La Conciergerie',
          subject: `Message propriétaire — ${nomBien} (${nomClient})`,
          htmlContent,
          replyTo: 'client@conciergerie-alsace.com',
        }),
      }).catch(e => console.warn('[FilCommentaires] Email notif échoué', e.message));
    }
  };

  const handleMarkRead = () => {
    setFil(prev=>(prev||[]).map(m=>{
      if (m.bid!==bid) return m;
      return isAdmin ? {...m, lu_admin:true} : {...m, lu_client:true};
    }));
  };

  const nonLus = msgs.filter(m=> isAdmin ? !m.lu_admin : !m.lu_client).length;
  const canWrite = !readOnly;

  return (
    <div style={{border:'1px solid var(--li)',borderRadius:10,overflow:'hidden'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',
        background:'var(--bg)',padding:'8px 12px',borderBottom:'1px solid var(--li)'}}>
        <div style={{fontWeight:700,fontSize:13}}>{icone} {titre}</div>
        {nonLus>0&&<button onClick={handleMarkRead} className="btn bgh sm" style={{fontSize:11}}>
          ✓ Tout lu ({nonLus})
        </button>}
      </div>

      {hint&&(
        <div style={{fontSize:11,color:'#1e5a8a',background:'#eef6fc',
          borderBottom:'1px solid #bfdbfe',padding:'6px 12px',lineHeight:1.5}}>
          {hint}
        </div>
      )}

      {disclaimer&&(
        <div style={{fontSize:11,color:'#92400e',background:'#fffbeb',
          borderBottom:'1px solid #fcd34d',padding:'6px 12px',lineHeight:1.5}}>
          ⚠️ {disclaimer}
        </div>
      )}

      <div style={{padding:'10px 12px',display:'flex',flexDirection:'column',gap:8,
        maxHeight:220,overflowY:'auto',background:'white'}}>
        {msgs.length===0
          ? <div style={{fontSize:12,color:'var(--si)',textAlign:'center',padding:'10px 0'}}>
              Aucun message pour le moment.
            </div>
          : msgs.map(m=>{
            const isMine = isAdmin ? m.auteurType==='admin' : m.auteurType==='client';
            const nonLu = isAdmin ? !m.lu_admin : !m.lu_client;
            return(
              <div key={m.id} style={{display:'flex',flexDirection:'column',
                alignItems:isMine?'flex-end':'flex-start'}}>
                <div style={{
                  maxWidth:'85%',
                  background:isMine?'#e8f4fd':'var(--bg)',
                  borderRadius:isMine?'12px 12px 3px 12px':'12px 12px 12px 3px',
                  padding:'7px 11px',fontSize:12,lineHeight:1.55,
                  border: nonLu&&!isMine ? '1.5px solid var(--mn)' : '1px solid var(--li)',
                }}>
                  {m.texte}
                </div>
                <div style={{fontSize:10,color:'var(--si)',marginTop:2,display:'flex',gap:5,alignItems:'center'}}>
                  <span>{m.auteur}</span>
                  <span>·</span>
                  <span>{new Date(m.ts).toLocaleDateString('fr-FR',{day:'2-digit',month:'2-digit'})} {new Date(m.ts).toLocaleTimeString('fr-FR',{hour:'2-digit',minute:'2-digit'})}</span>
                  {nonLu&&!isMine&&<span style={{width:6,height:6,borderRadius:'50%',background:'var(--mn)',display:'inline-block'}}/>}
                </div>
              </div>
            );
          })
        }
      </div>

      {canWrite&&(
        <div style={{display:'flex',gap:8,padding:'8px 12px',borderTop:'1px solid var(--li)',background:'var(--bg)'}}>
          <input
            type="text"
            value={texte}
            onChange={e=>setTexte(e.target.value)}
            onKeyDown={e=>{ if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();handleSend();} }}
            placeholder="Votre message…"
            style={{flex:1,fontSize:12}}
          />
          <button
            onClick={handleSend}
            disabled={saving||!texte.trim()}
            className="btn bmn sm"
            style={{fontSize:12}}
          >
            Envoyer
          </button>
        </div>
      )}
      {readOnly&&(
        <div style={{padding:'7px 12px',borderTop:'1px solid var(--li)',background:'var(--bg)',
          fontSize:11,color:'var(--si)',fontStyle:'italic'}}>
          👁 Lecture seule — les réponses sont envoyées par La Conciergerie.
        </div>
      )}
    </div>
  );
}

// ─── Fil commentaires internes (lecture seule, depuis commentaires8) ──
function FilInterne({ bid, commentaires, onNavCtrl }) {
  const msgs = useMemo(()=>
    (commentaires||[]).filter(c=>c.bid===bid).sort((a,b)=>b.ts.localeCompare(a.ts)).slice(0,10),
    [commentaires, bid]
  );

  const NIV = {
    info:     { label:'OK',       bg:'#e1f7ee', c:'#1e6e3d', e:'✅' },
    attention:{ label:'À noter',  bg:'#fff4d6', c:'#7a5a00', e:'⚠️' },
    incident: { label:'Incident', bg:'#fde0e0', c:'#8a1010', e:'🔴' },
  };

  return (
    <div style={{border:'1px solid var(--li)',borderRadius:10,overflow:'hidden'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',
        background:'var(--bg)',padding:'8px 12px',borderBottom:'1px solid var(--li)'}}>
        <div style={{fontWeight:700,fontSize:13}}>🔒 Commentaires internes</div>
        {onNavCtrl&&<button onClick={onNavCtrl} className="btn bgh sm" style={{fontSize:11}}>
          → Voir tous
        </button>}
      </div>
      <div style={{padding:'10px 12px',display:'flex',flexDirection:'column',gap:6,
        maxHeight:200,overflowY:'auto',background:'white'}}>
        {msgs.length===0
          ? <div style={{fontSize:12,color:'var(--si)',textAlign:'center',padding:'10px 0'}}>
              Aucun commentaire pour ce logement.
            </div>
          : msgs.map(c=>{
            const niv = NIV[c.niveau]||NIV.info;
            return (
              <div key={c.id} style={{
                borderLeft:`3px solid ${niv.c}`,
                background:niv.bg,
                borderRadius:6,padding:'6px 10px',
                opacity:c.lu?.85:1,
              }}>
                <div style={{display:'flex',gap:6,alignItems:'center',marginBottom:2,flexWrap:'wrap'}}>
                  <span style={{fontSize:11,fontWeight:700,color:niv.c}}>{niv.e} {niv.label}</span>
                  {!c.lu&&<span style={{width:6,height:6,borderRadius:'50%',background:'var(--bl)',display:'inline-block'}}/>}
                  <span style={{fontSize:10,color:'var(--si)',marginLeft:'auto'}}>
                    {new Date(c.ts).toLocaleDateString('fr-FR',{day:'2-digit',month:'2-digit'})} {new Date(c.ts).toLocaleTimeString('fr-FR',{hour:'2-digit',minute:'2-digit'})}
                  </span>
                </div>
                {c.texte&&<div style={{fontSize:12,lineHeight:1.5}}>{c.texte}</div>}
              </div>
            );
          })
        }
      </div>
      <div style={{padding:'6px 12px',borderTop:'1px solid var(--li)',background:'var(--bg)',
        fontSize:11,color:'var(--si)',fontStyle:'italic'}}>
        🔒 Non visible par le propriétaire
      </div>
    </div>
  );
}

// ─── Carte appartement ───────────────────────────────────────────
function CarteStatut({ bien, statut, prochain, arriveeAujourdhui: arrAujourd, isAdmin,
  statutAppts, setStatutAppts, filPartage, setFilPartage, filProprio, setFilProprio,
  commentaires, sess, nonLusProprioBid, onNav, clientNom, logAction }) {

  const [open, setOpen] = useState(false);
  // Onglets fils : 'partage' | 'proprio' | 'interne'
  const [tabFil, setTabFil] = useState('partage');

  // ─── Statut manuel ──────────────────────────────────────────────
  const STATUTS_FORCE = [
    { value: '',              label: '↩ Automatique (calculé)',  emoji: '⚙️'  },
    { value: 'force_pret',    label: 'Prêt',                     emoji: '🟢'  },
    { value: 'force_ctrl',    label: 'À contrôler',              emoji: '🟡'  },
    { value: 'force_men',     label: 'À nettoyer',               emoji: '🟠'  },
    { value: 'force_nonpret', label: 'Problème signalé',         emoji: '⚠️'  },
    { value: 'force_sortie',  label: 'Sortie en cours',          emoji: '🔴'  },
  ];

  const handleSetStatutManuel = (val) => {
    const LABELS_FORCE = {
      '': 'Automatique (calculé)', force_pret: 'Prêt', force_ctrl: 'À contrôler',
      force_men: 'À nettoyer', force_nonpret: 'Problème signalé', force_sortie: 'Sortie en cours'
    };
    const ancien = statutAppts?.[bien.id] || '';
    if (val === ancien) return;
    if (val === '') {
      const next = { ...statutAppts };
      delete next[bien.id];
      delete next[`force_ts_${bien.id}`];
      setStatutAppts(next);
    } else {
      // Horodatage de pose : sert à faire expirer l'override automatiquement
      // au prochain départ détecté pour ce bien (un override = un cycle).
      setStatutAppts(prev => ({ ...prev, [bien.id]: val, [`force_ts_${bien.id}`]: new Date().toISOString() }));
    }
    if (typeof logAction === 'function') logAction({
      action: 'override_statut',
      qui: sess?.name || (sess?.type === 'gerant' ? 'Direction' : 'Admin'),
      role: sess?.type || 'admin',
      bid: bien.id,
      objet: bien.name,
      detail: `${LABELS_FORCE[ancien] || ancien || 'Automatique'} → ${LABELS_FORCE[val] || val || 'Automatique'}`,
    });
  };

  const override = statutAppts?.[bien.id];
  const alerteProprio = isAdmin && nonLusProprioBid > 0;

  return (
    <div
      className="card"
      style={{
        borderLeft: `4px solid ${alerteProprio ? '#d97706' : statut.color}`,
        padding: '14px 16px',
        cursor: 'pointer',
        background: open ? 'var(--hov, #fafafa)' : undefined,
      }}
      onClick={() => setOpen(o => !o)}
    >
      {/* Bandeau alerte message proprio */}
      {alerteProprio && (
        <div
          style={{margin:'-14px -16px 10px -16px',background:'#fffbeb',
            borderBottom:'1px solid #fcd34d',padding:'6px 16px',
            fontSize:12,fontWeight:600,color:'#92400e',display:'flex',alignItems:'center',gap:6}}
          onClick={e=>{e.stopPropagation();setOpen(true);setTabFil('proprio');}}
        >
          <span>💬</span>
          <span>{nonLusProprioBid} nouveau{nonLusProprioBid>1?'x':''} message{nonLusProprioBid>1?'s':''} du propriétaire</span>
          <span style={{marginLeft:'auto',fontSize:11,color:'#b45309'}}>→ Voir</span>
        </div>
      )}

      {/* En-tête carte */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 22 }}>{statut.emoji}</span>
        <div style={{ flex: 1, minWidth: 140 }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>{bien.name}</div>
          {bien.addr && <div style={{ fontSize: 11, color: 'var(--si)' }}>{bien.addr}</div>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{
            background: statut.bg, color: statut.color,
            fontSize: 11, fontWeight: 700,
            padding: '3px 10px', borderRadius: 999, whiteSpace: 'nowrap',
          }}>
            {statut.code === 'sortie' ? `🔴 Sortie en cours — départ ${statut.heureDepart}` : statut.label}
          </span>
          {/* Pastille En cours */}
          {statut.enCours && (
            <span style={{background:'#dbeeff',color:'#1d4ed8',fontSize:11,fontWeight:700,
              padding:'3px 10px',borderRadius:999,whiteSpace:'nowrap'}}>
              🔵 En cours
            </span>
          )}
          {arrAujourd && (
            <span style={{background:'#dbeeff',color:'#1e5fa8',fontSize:11,fontWeight:700,
              padding:'3px 10px',borderRadius:999,whiteSpace:'nowrap'}}>
              🔑 Arrivée aujourd'hui
            </span>
          )}
        </div>
        <span style={{ fontSize: 18, color: 'var(--si)', marginLeft: 4 }}>{open ? '▲' : '▼'}</span>
      </div>

      {/* Info rapide */}
      {!open && (
        <div style={{ marginTop: 6, fontSize: 12, color: 'var(--si)', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {statut.code === 'sortie' && (
            <span>🚪 Sortie prévue à <strong>{statut.heureDepart}</strong></span>
          )}
          {statut.code === 'occ' && statut.sejour && (
            <span>🚪 Départ prévu : <strong>{fd(statut.sejour.dateDepart + 'T00:00:00')}</strong></span>
          )}
          {prochain && statut.code !== 'occ' && statut.code !== 'sortie' && (
            <span>🔑 Prochaine arrivée : <strong>{fd(prochain.date + 'T00:00:00')}{prochain.heure ? ` à ${prochain.heure}` : ''}</strong></span>
          )}
          {statut.rapport?.sub && statut.code === 'ctrl' && (
            <span>🧹 Ménage soumis le {new Date(statut.rapport.sub).toLocaleDateString('fr-FR')}</span>
          )}
        </div>
      )}

      {/* Détail au clic */}
      {open && (
        <div
          style={{ marginTop: 14, borderTop: '1px solid var(--li)', paddingTop: 12 }}
          onClick={e => e.stopPropagation()}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 13 }}>

            {/* Sortie en cours */}
            {statut.code === 'sortie' && (
              <div style={{ background: '#fde0e0', borderRadius: 8, padding: '8px 12px' }}>
                <div style={{ fontWeight: 700, marginBottom: 2 }}>🔴 Sortie en cours</div>
                <div>Départ prévu à <strong>{statut.heureDepart}</strong></div>
                <div style={{ fontSize: 11, color: 'var(--si)', marginTop: 2 }}>Le logement sera disponible pour le ménage après cette heure.</div>
              </div>
            )}

            {/* Séjour en cours — iCal */}
            {statut.sejour && (
              <div style={{ background: '#fde0e0', borderRadius: 8, padding: '8px 12px' }}>
                <div style={{ fontWeight: 700, marginBottom: 2 }}>🔴 Séjour en cours</div>
                <div>Du <strong>{fd(statut.sejour.dateArrivee + 'T00:00:00')}</strong> au <strong>{fd(statut.sejour.dateDepart + 'T00:00:00')}</strong></div>
                {statut.sejour.refEviivo && <div style={{ fontSize: 11, color: 'var(--si)' }}>Réf. : {statut.sejour.refEviivo}</div>}
              </div>
            )}

            {/* Séjour en cours — tâches/saisies */}
            {statut.code === 'occ' && !statut.sejour && statut.derniereArrivee && (
              <div style={{ background: '#fde0e0', borderRadius: 8, padding: '8px 12px' }}>
                <div style={{ fontWeight: 700, marginBottom: 2 }}>🔴 Occupé</div>
                <div>Arrivée le <strong>{fd(statut.derniereArrivee + 'T00:00:00')}</strong></div>
                {statut.prochainDepart
                  ? <div>Départ prévu le <strong>{fd(statut.prochainDepart + 'T00:00:00')}</strong></div>
                  : <div style={{ fontSize: 11, color: 'var(--si)' }}>Aucun départ saisi — à renseigner dans le planning.</div>
                }
              </div>
            )}

            {/* Ménage en cours */}
            {statut.enCours && (
              <div style={{ background: '#dbeeff', borderRadius: 8, padding: '8px 12px' }}>
                <div style={{ fontWeight: 700, marginBottom: 2, color:'#1d4ed8' }}>🔵 Ménage en cours</div>
                <div style={{ fontSize: 11, color: '#1d4ed8' }}>Un salarié a pointé le début du ménage sur ce logement.</div>
              </div>
            )}

            {/* Dernier séjour */}
            {statut.dernierSejour && statut.code !== 'occ' && statut.code !== 'sortie' && (
              <div style={{ background: '#f5f5f5', borderRadius: 8, padding: '8px 12px' }}>
                <div style={{ fontWeight: 600, marginBottom: 2 }}>Dernier départ</div>
                <div>{fd(statut.dernierSejour.dateDepart + 'T00:00:00')}</div>
              </div>
            )}

            {/* Arrivée aujourd'hui */}
            {arrAujourd && (
              <div style={{ background: '#dbeeff', borderRadius: 8, padding: '8px 12px' }}>
                <div style={{ fontWeight: 700, marginBottom: 2, color: '#1e5fa8' }}>🔑 Arrivée prévue aujourd'hui</div>
                <div style={{ fontSize: 11, color: '#1e5fa8' }}>Vérifiez que le logement est prêt avant le check-in.</div>
              </div>
            )}

            {/* Rapport de ménage — masqué pour "À nettoyer" (rapport antérieur au séjour) */}
            {statut.rapport && statut.code !== 'men' && (
              <div style={{ background: '#fff9e0', borderRadius: 8, padding: '8px 12px' }}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:6}}>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 2 }}>🧹 Rapport de ménage</div>
                    <div>Soumis le {new Date(statut.rapport.sub).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
                    {statut.rapport.notes && (
                      <div style={{ fontSize: 12, color: 'var(--si)', marginTop: 4, fontStyle: 'italic' }}>
                        « {statut.rapport.notes.slice(0, 120)}{statut.rapport.notes.length > 120 ? '…' : ''} »
                      </div>
                    )}
                  </div>
                  {isAdmin && onNav && (
                    <button
                      onClick={()=>onNav('rap')}
                      className="btn bgh sm"
                      style={{fontSize:11,whiteSpace:'nowrap'}}
                    >
                      → Voir le rapport
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Contrôle */}
            {statut.controleValide && statut.code !== 'men' && (
              <div style={{
                background: statut.controleValide.verdict === 'pret' ? '#e1f7ee'
                  : statut.controleValide.verdict === 'abandonne' ? '#f0f0f0' : '#fde0e0',
                borderRadius: 8, padding: '8px 12px',
              }}>
                <div style={{ fontWeight: 600, marginBottom: 2 }}>
                  {statut.controleValide.verdict === 'pret' ? '✅ Contrôle validé'
                    : statut.controleValide.verdict === 'abandonne' ? '🗑 Contrôle abandonné' : '⚠️ Contrôle non conforme'}
                </div>
                {statut.controleValide.closedAt && (
                  <div style={{ fontSize: 12 }}>Le {new Date(statut.controleValide.closedAt).toLocaleDateString('fr-FR')}</div>
                )}
                {statut.controleValide.notes && (
                  <div style={{ fontSize: 12, color: 'var(--si)', marginTop: 4, fontStyle: 'italic' }}>
                    « {statut.controleValide.notes.slice(0, 120)}{statut.controleValide.notes.length > 120 ? '…' : ''} »
                  </div>
                )}
              </div>
            )}

            {/* Prochaine arrivée */}
            {prochain && statut.code !== 'sortie' && (
              <div style={{ background: '#dbeeff', borderRadius: 8, padding: '8px 12px' }}>
                <div style={{ fontWeight: 600, marginBottom: 2 }}>🔑 Prochaine arrivée</div>
                <div>{fd(prochain.date + 'T00:00:00')}{prochain.heure ? ` à ${prochain.heure}` : ''}</div>
              </div>
            )}
            {/* Actions admin — sélecteur de statut manuel */}
            {isAdmin && (
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: 12, color: 'var(--si)', marginBottom: 5, fontWeight: 600 }}>
                  🎛️ Forcer le statut manuellement
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <select
                    value={override && ['force_pret','force_ctrl','force_men','force_nonpret','force_sortie'].includes(override) ? override : ''}
                    onChange={e => handleSetStatutManuel(e.target.value)}
                    style={{
                      fontSize: 13, padding: '6px 10px', borderRadius: 8,
                      border: override ? '2px solid var(--mn)' : '1px solid var(--li)',
                      background: override ? '#fff8f0' : 'var(--bg)',
                      fontWeight: override ? 700 : 400,
                      cursor: 'pointer', minWidth: 220,
                    }}
                  >
                    {STATUTS_FORCE.map(s => (
                      <option key={s.value} value={s.value}>{s.emoji} {s.label}</option>
                    ))}
                  </select>
                  {override && (
                    <span style={{ fontSize: 11, color: '#b45309', fontStyle: 'italic' }}>
                      ⚠️ Statut forcé manuellement
                    </span>
                  )}
                </div>
                {override && (
                  <div style={{ fontSize: 11, color: 'var(--si)', marginTop: 5 }}>
                    Sélectionnez « ↩ Automatique » pour revenir au calcul automatique.
                  </div>
                )}
                {/* Toggle "Ménage en cours" */}
                <div style={{ marginTop: 10 }}>
                  <button
                    onClick={() => {
                      const key = `force_encours_${bien.id}`;
                      if (statutAppts?.[key]) {
                        const next = { ...statutAppts };
                        delete next[key];
                        setStatutAppts(next);
                      } else {
                        setStatutAppts(prev => ({ ...prev, [key]: true }));
                      }
                    }}
                    className="btn bor sm"
                    style={{
                      borderColor: statutAppts?.[`force_encours_${bien.id}`] ? '#1d4ed8' : 'var(--li)',
                      color: statutAppts?.[`force_encours_${bien.id}`] ? '#1d4ed8' : 'var(--si)',
                      background: statutAppts?.[`force_encours_${bien.id}`] ? '#dbeeff' : 'var(--bg)',
                      fontWeight: statutAppts?.[`force_encours_${bien.id}`] ? 700 : 400,
                    }}
                  >
                    🔵 Ménage en cours {statutAppts?.[`force_encours_${bien.id}`] ? '(actif — cliquer pour désactiver)' : '(inactif — cliquer pour activer)'}
                  </button>
                </div>
              </div>
            )}

            {/* ─── Fils de commentaires ─── */}
            {(filPartage!==null || filProprio!==null || (isAdmin && commentaires)) && (
              <div style={{marginTop:8,borderTop:'1px solid var(--li)',paddingTop:12}}>

                {/* Onglets */}
                <div style={{display:'flex',gap:6,marginBottom:10,flexWrap:'wrap'}}>
                  {filPartage!==null&&(
                    <button onClick={()=>setTabFil('partage')}
                      className={`btn ${tabFil==='partage'?'bmn':'bgh'} sm`} style={{fontSize:12}}>
                      📢 Partagé
                    </button>
                  )}
                  {filProprio!==null&&(
                    <button onClick={()=>setTabFil('proprio')}
                      className={`btn ${tabFil==='proprio'?'bmn':'bgh'} sm`}
                      style={{fontSize:12,position:'relative'}}>
                      💬 Propriétaire
                      {isAdmin && nonLusProprioBid>0 && (
                        <span style={{
                          position:'absolute',top:-6,right:-6,
                          background:'var(--pk)',color:'white',
                          borderRadius:'50%',width:16,height:16,
                          fontSize:9,fontWeight:700,
                          display:'flex',alignItems:'center',justifyContent:'center',
                        }}>{nonLusProprioBid}</span>
                      )}
                    </button>
                  )}
                  {isAdmin && commentaires && (
                    <button onClick={()=>setTabFil('interne')}
                      className={`btn ${tabFil==='interne'?'bmn':'bgh'} sm`} style={{fontSize:12}}>
                      🔒 Interne
                    </button>
                  )}
                </div>

                {tabFil==='partage'&&filPartage!==null&&(
                  <FilCommentaires
                    bid={bien.id}
                    fil={filPartage}
                    setFil={setFilPartage}
                    isAdmin={isAdmin}
                    titre="Messages partagés"
                    icone="📢"
                    disclaimer={null}
                    readOnly={!isAdmin}
                    hint={!isAdmin ? "📢 Annonces et informations transmises par La Conciergerie au sujet de ce logement. Pour nous écrire, utilisez l'onglet « Propriétaire »." : null}
                  />
                )}

                {tabFil==='proprio'&&filProprio!==null&&(
                  <FilCommentaires
                    bid={bien.id}
                    fil={filProprio}
                    setFil={setFilProprio}
                    isAdmin={isAdmin}
                    titre="Messages propriétaire"
                    icone="💬"
                    disclaimer="Les messages envoyés ici ne constituent pas un accord contractuel. Toute demande engageante doit être confirmée par écrit (email ou courrier)."
                    readOnly={false}
                    clientNom={clientNom}
                    bienNom={bien.name}
                    hint={!isAdmin ? "💬 Échangez directement avec La Conciergerie au sujet de ce logement. Nous sommes notifiés de chaque message." : null}
                  />
                )}

                {tabFil==='interne'&&isAdmin&&commentaires&&(
                  <FilInterne
                    bid={bien.id}
                    commentaires={commentaires}
                    onNavCtrl={onNav ? ()=>onNav('com') : null}
                  />
                )}
              </div>
            )}

          </div>
        </div>
      )}
    </div>
  );
}

// ─── Composant principal ─────────────────────────────────────────
function SecStatutAppts({ biens, clients, sejours, rep, controles, statutAppts, setStatutAppts, logAction,
  sess, st, als, filPartage, setFilPartage, filProprio, setFilProprio, commentaires, onNav }) {

  const isAdmin = sess?.type === 'admin';
  const todayStr = today();

  const biensAffichables = useMemo(() => {
    const clientsSuivi = new Set((clients||[]).filter(c=>c.suiviStatut).map(c=>c.id));
    if (isAdmin) return (biens || []).filter(b => clientsSuivi.has(b.cid));
    return (biens || []).filter(b => b.cid === sess?.id && !b.bienAnnexe && clientsSuivi.has(b.cid));
  }, [biens, clients, isAdmin, sess]);

  const [filterClient, setFilterClient] = useState('all');
  const [filterStatut, setFilterStatut] = useState(null);

  const clientsAvecBiens = useMemo(() => {
    if (!isAdmin) return [];
    return (clients || []).filter(c => biensAffichables.some(b => b.cid === c.id));
  }, [clients, biensAffichables, isAdmin]);

  const biensFiltresParClient = useMemo(() => {
    if (!isAdmin) return biensAffichables;
    if (filterClient === 'all') return biensAffichables;
    return biensAffichables.filter(b => b.cid === filterClient);
  }, [biensAffichables, filterClient, isAdmin]);

  const statuts = useMemo(() => {
    const result = {};
    biensFiltresParClient.forEach(b => {
      result[b.id] = calcStatutBien(b.id, sejours, rep, controles, statutAppts, st, als);
    });
    return result;
  }, [biensFiltresParClient, sejours, rep, controles, statutAppts, st, als]);

  const prochainsArr = useMemo(() => {
    const result = {};
    biensFiltresParClient.forEach(b => {
      result[b.id] = prochainSejour(b.id, sejours, st, statutAppts, als);
    });
    return result;
  }, [biensFiltresParClient, sejours, st, statutAppts, als]);

  const arriveeAujourdhuiMap = useMemo(() => {
    const result = {};
    biensFiltresParClient.forEach(b => {
      result[b.id] = arriveeAujourdhui(b.id, todayStr, sejours, st, statutAppts);
    });
    return result;
  }, [biensFiltresParClient, todayStr, sejours, st, statutAppts]);

  const compteurs = useMemo(() => {
    const c = { occ: 0, sortie: 0, men: 0, ctrl: 0, pret: 0, nonpret: 0 };
    Object.values(statuts).forEach(s => { c[s.code] = (c[s.code] || 0) + 1; });
    return c;
  }, [statuts]);

  const biensFiltres = useMemo(() => {
    if (!filterStatut) return biensFiltresParClient;
    return biensFiltresParClient.filter(b => statuts[b.id]?.code === filterStatut);
  }, [biensFiltresParClient, filterStatut, statuts]);

  const nonLusProprioMap = useMemo(() => {
    if (!isAdmin || !filProprio) return {};
    const map = {};
    (filProprio||[]).forEach(m=>{
      if (!m.lu_admin) map[m.bid] = (map[m.bid]||0)+1;
    });
    return map;
  }, [isAdmin, filProprio]);

  if (biensAffichables.length === 0) {
    return (
      <div className="empty">
        {isAdmin ? 'Aucun bien configuré.' : 'Aucun bien associé à votre compte.'}
      </div>
    );
  }

  const STATUTS_DEF = [
    { code: 'sortie', emoji: '🔴', label: 'Sortie en cours', color: '#dc2626', bg: '#fde0e0' },
    { code: 'occ',    emoji: '🔴', label: 'Occupé',          color: '#dc2626', bg: '#fde0e0' },
    { code: 'men',    emoji: '🟠', label: 'À nettoyer',       color: '#c2410c', bg: '#ffe9d6' },
    { code: 'ctrl',   emoji: '🟡', label: 'À contrôler',      color: '#b45309', bg: '#fef3c7' },
    { code: 'nonpret',emoji: '⚠️', label: 'Problème signalé', color: '#b91c1c', bg: '#fde0e0' },
    { code: 'pret',   emoji: '🟢', label: 'Prêt',             color: '#16a34a', bg: '#e1f7ee' },
  ];

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h2 className="D" style={{ fontSize: 22, marginBottom: 4 }}>Statut des appartements</h2>
        <p style={{ fontSize: 13, color: 'var(--si)', marginBottom: 12 }}>Suivi en temps réel de l'état de chaque logement.</p>

        {/* Pastilles résumé cliquables */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
          {STATUTS_DEF.map(s => {
            if (compteurs[s.code] === 0) return null;
            const actif = filterStatut === s.code;
            return (
              <button
                key={s.code}
                onClick={() => setFilterStatut(actif ? null : s.code)}
                style={{
                  background: actif ? s.color : s.bg,
                  color: actif ? 'white' : s.color,
                  fontSize: 12, fontWeight: 700,
                  padding: '4px 12px', borderRadius: 999,
                  border: `2px solid ${s.color}`,
                  cursor: 'pointer', transition: 'all .15s', outline: 'none',
                }}
              >
                {s.emoji} {compteurs[s.code]} {s.label}
              </button>
            );
          })}
          {filterStatut && (
            <button
              onClick={() => setFilterStatut(null)}
              style={{fontSize:11,color:'var(--si)',background:'none',border:'1px solid var(--li)',
                borderRadius:999,padding:'4px 10px',cursor:'pointer'}}
            >
              ✕ Tous
            </button>
          )}
        </div>

        {/* Filtre propriétaire */}
        {isAdmin && clientsAvecBiens.length > 1 && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
            <span style={{ fontSize: 12, color: 'var(--si)', alignSelf: 'center' }}>Propriétaire :</span>
            <button onClick={() => setFilterClient('all')} className={`btn ${filterClient === 'all' ? 'bmn' : 'bgh'} sm`}>Tous</button>
            {clientsAvecBiens.map(c => (
              <button key={c.id} onClick={() => setFilterClient(c.id)} className={`btn ${filterClient === c.id ? 'bmn' : 'bgh'} sm`}>{c.name}</button>
            ))}
          </div>
        )}

        {filterStatut && biensFiltres.length === 0 && (
          <div style={{fontSize:13,color:'var(--si)',padding:'12px 0'}}>
            Aucun appartement dans cet état en ce moment.
          </div>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {biensFiltres
          .filter(b => !b.bienAnnexe)
          .sort((a, b) => (a.rangFacturation ?? 999) - (b.rangFacturation ?? 999))
          .map(b => (
            <CarteStatut
              key={b.id}
              bien={b}
              statut={statuts[b.id] || { code: 'pret', emoji: '🟢', label: 'Prêt', color: '#16a34a', bg: '#e1f7ee' }} logAction={logAction}
              prochain={prochainsArr[b.id]}
              arriveeAujourdhui={arriveeAujourdhuiMap[b.id]}
              isAdmin={isAdmin}
              statutAppts={statutAppts}
              setStatutAppts={setStatutAppts}
              filPartage={filPartage || null}
              setFilPartage={setFilPartage || null}
              filProprio={filProprio || null}
              setFilProprio={setFilProprio || null}
              commentaires={commentaires || null}
              sess={sess}
              nonLusProprioBid={nonLusProprioMap[b.id] || 0}
              onNav={onNav || null}
              clientNom={(clients||[]).find(c=>c.id===b.cid)?.name||''}
            />
          ))}
      </div>
    </div>
  );
}

export { SecStatutAppts };

